import { forwardRef, useState, useEffect } from 'react';
import { Heading, Text, Card, Flex, Grid, Box, Container, Button, Section } from '@radix-ui/themes';
import * as Accordion from '@radix-ui/react-accordion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullseye, faChampagneGlasses, faWandMagicSparkles, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import config from '../../config';
import './About.css';
import SEO from '../SEO/SEO';
import { localBusinessSchema } from '../../utils/structuredData';
import { useSiteConfig } from '../../utils/siteConfig';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  sortOrder: number;
  active: boolean;
}

const About = forwardRef<HTMLDivElement>((_, ref) => {
  const [faqItems, setFaqItems] = useState<FAQItem[]>([]);
  const [faqLoading, setFaqLoading] = useState(true);
  // Photo URLs come from siteConfig (set in the Site Content admin tab,
  // or directly as a CDN URL pointing into DO Spaces). Empty values
  // suppress the photo block on the page rather than showing a broken
  // image.
  const siteConfig = useSiteConfig();
  const storyPhotoUrl = siteConfig.get('aboutPhoto1Url', '');
  const differencePhotoUrl = siteConfig.get('aboutPhoto2Url', '');
  const hostHeadshotUrl = siteConfig.get(
    'aboutHeadshotUrl',
    'https://triviakeg.nyc3.cdn.digitaloceanspaces.com/IMG_6929.jpg',
  );

  useEffect(() => {
    const loadFaq = async () => {
      try {
        const res = await axios.get(`${config.apiBaseUrl}/faq`);
        setFaqItems(res.data.data.items ?? []);
      } catch (error) {
        console.error('Error loading FAQ:', error);
      } finally {
        setFaqLoading(false);
      }
    };
    loadFaq();
  }, []);

  return (
    <div ref={ref} className="about">
      <SEO
        title="About West Slope Quiz | Grand Junction's Pub Trivia Hosts"
        description="Learn about West Slope Quiz — Grand Junction's premier pub trivia company. Weekly events across the Grand Valley, plus private parties, corporate team building, and fundraisers. Hosted by Ryan Fleming."
        canonical="/about"
        structuredData={localBusinessSchema(siteConfig.get('brandName', 'West Slope Quiz'))}
      />

      {/* Hero Section */}
      <section className="about-hero">
        <Container size="4">
          <Flex direction="column" align="center" gap="4">
            <Heading size="8" className="about-title">West Slope Quiz</Heading>
            <Text size={{ initial: '4', md: '5' }} className="about-tagline">
              Bringing fun, fresh, and unforgettable quiz nights to the Grand Valley
            </Text>
          </Flex>
        </Container>
      </section>

      {/* What We Offer */}
      <Section className="about-section services-section">
        <Container size="4">
          <Heading size="6" align="center" mb="6" className="section-title">What We Offer</Heading>
          <Grid columns={{ initial: '1', md: '3' }} gap="5" className="services-grid">
            <Card className="service-card">
              <Flex direction="column" align="center" gap="3">
                <Box className="service-icon">
                  <FontAwesomeIcon icon={faBullseye} />
                </Box>
                <Heading size="4" as="h3" className="accent-heading">Weekly Pub Trivia</Heading>
                <Text color="gray" align="center">
                  Join us at local venues for exciting weekly trivia nights. Fresh questions every
                  week, welcoming atmosphere, and prizes for the winners!
                </Text>
              </Flex>
            </Card>

            <Card className="service-card">
              <Flex direction="column" align="center" gap="3">
                <Box className="service-icon">
                  <FontAwesomeIcon icon={faChampagneGlasses} />
                </Box>
                <Heading size="4" as="h3" className="accent-heading">Private Events</Heading>
                <Text color="gray" align="center">
                  Perfect for corporate team-building, birthday parties, fundraisers, and special
                  occasions. Custom trivia tailored to your group!
                </Text>
              </Flex>
            </Card>

            <Card className="service-card">
              <Flex direction="column" align="center" gap="3">
                <Box className="service-icon">
                  <FontAwesomeIcon icon={faWandMagicSparkles} />
                </Box>
                <Heading size="4" as="h3" className="accent-heading">Custom Quizzes</Heading>
                <Text color="gray" align="center">
                  Themed trivia nights, branded experiences, and specialty rounds designed
                  specifically for your venue or event.
                </Text>
              </Flex>
            </Card>
          </Grid>
        </Container>
      </Section>

      {/* Our Story */}
      <Section className="about-section story-section">
        <Container size="4">
          <Flex direction={{ initial: 'column', md: 'row' }} gap="6" align="center" className="story-content">
            <Box className="story-text" style={{ flex: 1 }}>
              <Heading size="6" mb="4" className="section-title" style={{ textAlign: 'left' }}>Our Story</Heading>
              <Text as="p" size={{ initial: '3', md: '4' }} mb="3">
                West Slope Quiz is bringing a fresh take on pub trivia to the Grand Valley!
                We&apos;re creating engaging quiz nights for party-goers, mountain bikers, college
                students, Kiwanis club members, brew enthusiasts, couples, and anyone lucky enough
                to stop by our valley.
              </Text>
              <Text as="p" size={{ initial: '3', md: '4' }}>
                As the newest trivia company in town, we&apos;re building an exciting collection of
                original questions and fostering a vibrant trivia community that brings fun and
                friendly competition to local venues.
              </Text>
            </Box>
            {storyPhotoUrl && (
              <Box className="story-image" style={{ flex: 1 }}>
                <img src={storyPhotoUrl} alt="West Slope Quiz night" />
              </Box>
            )}
          </Flex>
        </Container>
      </Section>

      {/* What Makes Us Different */}
      <Section className="about-section difference-section">
        <Container size="4">
          <Flex direction={{ initial: 'column', md: 'row' }} gap="6" align="center" className="difference-content">
            {differencePhotoUrl && (
              <Box className="difference-image" style={{ flex: 1 }}>
                <img src={differencePhotoUrl} alt="Trivia players having fun" />
              </Box>
            )}
            <Box className="difference-text" style={{ flex: 1 }}>
              <Heading size="6" mb="4" className="section-title" style={{ textAlign: 'left' }}>What Makes Us Different</Heading>
              <Text as="p" size={{ initial: '3', md: '4' }} mb="3">
                Our goal is to deliver well-rounded trivia that goes beyond simply reading off
                factoids with question marks. Not everyone is a history buff, a movie expert, or a
                literature fanatic — and that&apos;s okay!
              </Text>
              <Text as="p" size={{ initial: '3', md: '4' }}>
                Our quizzes are designed to be inclusive, challenging, and engaging for all types
                of knowledge enthusiasts. Whether you&apos;re a seasoned quiz player or a
                first-timer, West Slope Quiz creates an exciting, friendly atmosphere where you
                can test your wits and enjoy the night.
              </Text>
            </Box>
          </Flex>
        </Container>
      </Section>

      {/* Meet Your Host */}
      <Section className="about-section host-section">
        <Container size="3">
          <Heading size="6" align="center" mb="6" className="section-title">Meet Your Host</Heading>
          <Flex direction={{ initial: 'column', md: 'row' }} gap="5" align="center" className="host-content">
            <Box className="host-image">
              <img src={hostHeadshotUrl} alt="Ryan Fleming" />
            </Box>
            <Box className="host-bio">
              <Heading size="5" as="h3" className="accent-heading" mb="2">Ryan Fleming</Heading>
              <Text color="gray" size={{ initial: '3', md: '4' }}>
                Ryan has hosted trivia nights across the Grand Valley for over three years, writing original questions and building a reputation for games that are accessible but challenging. Expect some niche topics, fair scoring, and at least one round that makes everyone groan.
              </Text>
            </Box>
          </Flex>
        </Container>
      </Section>

      {/* FAQ */}
      {!faqLoading && faqItems.length > 0 && (
        <Section className="about-section faq-section">
          <Container size="3">
            <Heading size="6" align="center" mb="6" className="section-title">Frequently Asked Questions</Heading>
            <Accordion.Root type="single" collapsible className="faq-accordion">
              {faqItems.map((item) => (
                <Accordion.Item key={item.id} value={`faq-${item.id}`} className="faq-item">
                  <Accordion.Header className="faq-header">
                    <Accordion.Trigger className="faq-trigger">
                      <span>{item.question}</span>
                      <FontAwesomeIcon icon={faChevronDown} className="faq-chevron" />
                    </Accordion.Trigger>
                  </Accordion.Header>
                  <Accordion.Content className="faq-content">
                    <Text as="p" size="3" className="faq-answer">
                      {item.answer}
                    </Text>
                  </Accordion.Content>
                </Accordion.Item>
              ))}
            </Accordion.Root>
          </Container>
        </Section>
      )}

      {/* Call to Action */}
      <section className="about-section cta-section">
        <Container size="2">
          <Flex direction="column" align="center" gap="4" className="cta-content">
            <Heading size="6" style={{ color: 'white' }}>Ready to Get Started?</Heading>
            <Text size="4" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
              Bring the fun to your venue or event. Get in touch and let&apos;s create an
              unforgettable trivia experience together!
            </Text>
            <Button size="3" variant="solid" asChild className="cta-button">
              <a href="/workwithus">Get in Touch</a>
            </Button>
          </Flex>
        </Container>
      </section>
    </div>
  );
});

About.displayName = 'About';

export default About;
